import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
// import { ArrowRight } from "lucide-react";
import ScrollReveal from "./ScrollReveal";

const contentData = {
  Secure: {
    title: "Secure what matters data, users, and business.",
    description: [
      "The digital landscape has erased boundaries. Data moves everywhere, work happens from anywhere, and attackers evolve rapidly while AI accelerates innovation and introduces new risk.",
      "Synthera Security delivers a cloud-first, Zero Trust security foundation that protects data, secures users, and safeguards the business, enabling organizations to adopt AI confidently and operate securely in a constantly evolving threat environment.",
    ],
    imgSrc:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1765979269/SECURE_p4l1to.png",
  },
  Transform: {
    title: "Transform your tomorrow through Secure AI.",
    description: [
      "AI is accelerating innovation while introducing new security, privacy, and compliance risks. Shadow AI, evolving regulations, prompt-based attacks, and autonomous agents expand the attack surface beyond what traditional security can handle.",
      "Synthera Security provides end-to-end AI security and governance, securing models, data, and agents across the AI lifecycle to enable safe, compliant adoption and confident innovation today and into an autonomous future.",
    ],
    imgSrc:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1765979265/AI_acwkbk.png",
  },
  Analyze: {
    title: "Analyze data into operational intelligence, everywhere.",
    description: [
      "In today’s digitally accelerated world, data drives competitive advantage, but most organizations struggle to turn scattered data into actionable intelligence.",
      "Synthera Security transforms scattered telemetry into real-time operational intelligence by continuously analyzing network paths, identity activity, user behavior, threat signals and AI performance metrics, enabling clear visibility, faster decision-making, and confident action at scale.",
    ],
    imgSrc:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1766044748/data_ana_img_oy8cue.png",
  },
};

const VisionSection = () => {
  const [activeTab, setActiveTab] = useState("Secure");
  const activeContent = contentData[activeTab];
  const tabs = ["Secure", "Transform", "Analyze"];

  const textAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  return (
    <ScrollReveal>
      <div className="bg-[#032d28] w-full min-h-screen flex items-center justify-center px-6 sm:px-8 lg:px-12 py-20 lg:py-0 font-sans text-gray-300 relative overflow-hidden">
        {" "}
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_30%,_rgba(0,192,139,0.1),_transparent_60%)]"></div>
        <div className="w-full max-w-7xl mx-auto z-10">
          <div className="flex flex-col lg:flex-row items-start justify-center gap-10 lg:gap-20">
            {/* Left Column */}
            <div className="w-full lg:w-1/2 text-left flex flex-col items-start">
              {/* Tabs - Now Responsive with flex-wrap */}
              <div className="flex flex-wrap items-center justify-start gap-y-4 gap-x-2 mb-8 text-lg font-normal tracking-widest uppercase text-white">
                {tabs.map((tab, index) => (
                  <React.Fragment key={tab}>
                    <button
                      onClick={() => setActiveTab(tab)}
                      className={`transition-opacity duration-300 ${
                        activeTab === tab
                          ? "opacity-100 font-bold"
                          : "opacity-60 hover:opacity-100"
                      }`}
                    >
                      {tab}
                    </button>
                    {index < tabs.length - 1 && (
                      <span className="text-white/40 mx-1 sm:mx-2 select-none">
                        •••••
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  variants={textAnimation}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {/* HEADER STYLE: Cato uses semi-bold (600) and tight tracking */}
                  <h2 className="text-4xl sm:text-5xl lg:text-[50px] font-semibold text-white leading-[1.1] tracking-[-0.02em] mb-8">
                    {activeContent.title}
                  </h2>

                  {/* BODY STYLE: Clean gray, larger line-height (relaxed), and specific size */}
                  <div className="max-w-xl space-y-6">
                    {activeContent.description.map((paragraph, index) => (
                      <p
                        key={index}
                        className="text-[19px] leading-[1.6] text-[#c0c7c6] font-normal"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <Link
                to="/Solutions"
                className="inline-flex items-center gap-3 mt-10 py-2.5 px-7 bg-[#00C08B] text-slate-900 rounded-full font-bold text-lg hover:bg-white transition-all duration-300 shadow-lg active:scale-95 no-underline"
              >
                Explore Our Solutions →
              </Link>
            </div>

            {/* Right Column */}
            <div className="w-full lg:w-1/2 mt-4 lg:mt-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                className="relative"
              >
                <div className="absolute -inset-2 bg-gradient-to-r from-teal-600 to-green-500 rounded-3xl blur-xl opacity-25"></div>

                <div className="relative aspect-video w-full">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeContent.imgSrc}
                      src={activeContent.imgSrc}
                      alt={activeContent.title}
                      className="w-full h-full object-cover object-[50%_30%] rounded-2xl"
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8 }}
                    />
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default VisionSection;
